import { Box, Divider, Stack, Typography } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import localeData from "dayjs/plugin/localeData";
import {
  AreaMaterialSnapshot,
  ScrapEyesEvent,
} from "components/scrapEyeLabelling/types";
import { VideoTimelineEventLane } from "./VideoTimelineEventLane";
import { ShortLabelDetail } from "./VideoTimelineShortLabelTrack";
import { TimelineMarkers } from "./TimelineMarkers";
import {
  TimelineZoomProvider,
  useTimelineWheelZoom,
  useTimelineZoom,
} from "../contexts/TimelineZoomContext";
import { ReactNode, useCallback, useMemo, useRef } from "react";
import { TimelineAreaMappingMarker } from "./TimelineAreaMappingMarker";
import { LiveTimeCaption, LiveTimelineCursor } from "./LiveTimelineCursor";

dayjs.extend(localeData);

interface VideoTimelineProps {
  currentTime: number;
  duration: number;
  events?: ScrapEyesEvent[];
  adjacentPreviewById?: ReadonlyMap<string, ReactNode>;
  readOnly?: boolean;
  selectedEventId?: string | null;
  onSeek: (time: number) => void;
  onEventClick?: (eventId: string) => void;
  videoStart?: Dayjs;
  colorEventsBy?: "type" | "material";
  materialNameSource?: "labelling" | "raw";
  children?: ReactNode;
  disableZoom?: boolean;
  areaMaterialSnapshots?: AreaMaterialSnapshot[];
  location?: string;
  onMappingSnapshotClick?: (snapshot: AreaMaterialSnapshot) => void;
  selectedSnapshotTimestamp?: number | null;
  showShortLabels?: boolean;
  shortLabelDetail?: ShortLabelDetail;
  modifiedEventIds?: Set<string>;
  highlightColorsByEventId?: ReadonlyMap<string, string | null>;
  highlightedLane?: string;
}

export const VideoTimeline = ({
  disableZoom,
  areaMaterialSnapshots,
  onMappingSnapshotClick,
  selectedSnapshotTimestamp,
  showShortLabels,
  ...props
}: VideoTimelineProps) => {
  const { duration, videoStart } = props;

  const existingContext = useTimelineZoom();

  // A read-only timeline renders as a static snapshot: no seek cursor, no zoom,
  // and no ambient zoom context — so it can be embedded (e.g. in a tooltip) without
  // coupling to the surrounding video player's playhead or zoom state.
  if (props.readOnly) {
    return (
      <VideoTimelineInner
        {...props}
        areaMaterialSnapshots={areaMaterialSnapshots}
        onMappingSnapshotClick={onMappingSnapshotClick}
        selectedSnapshotTimestamp={selectedSnapshotTimestamp}
        showShortLabels={showShortLabels}
      />
    );
  }

  if (!existingContext && !disableZoom) {
    return (
      <TimelineZoomProvider fullDuration={duration} videoStart={videoStart}>
        <VideoTimelineInner
          {...props}
          duration={duration}
          areaMaterialSnapshots={areaMaterialSnapshots}
          onMappingSnapshotClick={onMappingSnapshotClick}
          selectedSnapshotTimestamp={selectedSnapshotTimestamp}
          showShortLabels={showShortLabels}
        />
      </TimelineZoomProvider>
    );
  }

  return (
    <VideoTimelineInner
      {...props}
      areaMaterialSnapshots={areaMaterialSnapshots}
      onMappingSnapshotClick={onMappingSnapshotClick}
      selectedSnapshotTimestamp={selectedSnapshotTimestamp}
      showShortLabels={showShortLabels}
    />
  );
};

const VideoTimelineInner = ({
  currentTime,
  events,
  adjacentPreviewById,
  selectedEventId,
  onSeek,
  onEventClick,
  videoStart,
  children,
  duration,
  colorEventsBy,
  materialNameSource,
  areaMaterialSnapshots,
  onMappingSnapshotClick,
  selectedSnapshotTimestamp,
  showShortLabels,
  shortLabelDetail,
  modifiedEventIds,
  highlightColorsByEventId,
  highlightedLane,
  readOnly,
  location,
}: Omit<VideoTimelineProps, "disableZoom">) => {
  if (events && !videoStart) {
    throw new Error("Video start is required when events are present");
  }

  const timelineRef = useRef<HTMLDivElement>(null);

  const ambientZoomContext = useTimelineZoom();
  const zoomContext = readOnly ? null : ambientZoomContext;

  const visibleStart = zoomContext?.visibleStart ?? videoStart;
  const visibleEnd =
    zoomContext?.visibleEnd ?? videoStart?.add(duration, "second");
  const visibleDuration = zoomContext?.duration ?? duration;

  const visibleStartSeconds =
    visibleStart && videoStart ? visibleStart.diff(videoStart, "second") : 0;

  const formatTime = useCallback(
    (seconds: number) => {
      if (videoStart) {
        return videoStart.add(seconds, "second").format("L LTS");
      } else {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, "0")}`;
      }
    },
    [videoStart]
  );

  const formatTimeWithMillis = useCallback(
    (seconds: number) => {
      if (videoStart) {
        const cursorTime = videoStart.add(seconds, "second");
        const timeWithMillisFormat = cursorTime
          .localeData()
          .longDateFormat("LTS")
          .replace("ss", "ss.SSS");
        return cursorTime.format(`L ${timeWithMillisFormat}`);
      } else {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        const millis = Math.floor((seconds % 1) * 1000);
        return `${mins}:${secs.toString().padStart(2, "0")}.${millis
          .toString()
          .padStart(3, "0")}`;
      }
    },
    [videoStart]
  );

  const { onMouseMove: handleMouseMove, onMouseLeave: handleMouseLeave } =
    useTimelineWheelZoom(timelineRef, videoStart, !readOnly);

  const visibleEvents = useMemo(() => {
    if (!events || !visibleStart || !visibleEnd) return undefined;
    return events.filter((event) => {
      return (
        event.end.isAfter(visibleStart) && event.start.isBefore(visibleEnd)
      );
    });
  }, [events, visibleStart, visibleEnd]);

  const lanes = useMemo(
    () => [...new Set((events ?? []).map((event) => event.lane))],
    [events]
  );

  const visibleLanes = useMemo(
    () =>
      lanes
        .map((lane) => ({
          lane,
          events: (visibleEvents ?? []).filter((event) => event.lane === lane),
        }))
        .filter(
          ({ lane, events: laneEvents }) =>
            lane !== undefined || laneEvents.length > 0
        ),
    [lanes, visibleEvents]
  );

  const activeEventIds = useMemo<ReadonlySet<string>>(() => {
    if (!visibleEvents || !videoStart) return new Set();
    return new Set(
      visibleEvents
        .filter((event) => {
          const startSeconds = event.start.diff(videoStart, "second", true);
          const endSeconds = event.end.diff(videoStart, "second", true);
          return currentTime >= startSeconds && currentTime <= endSeconds;
        })
        .map((event) => event.id)
    );
  }, [visibleEvents, videoStart, currentTime]);

  // Filter snapshots to those within the visible window
  const visibleSnapshots = useMemo(() => {
    if (!areaMaterialSnapshots || !visibleStart || !visibleEnd)
      return undefined;
    return areaMaterialSnapshots.filter(
      (snapshot) =>
        !snapshot.timestamp.isBefore(visibleStart) &&
        !snapshot.timestamp.isAfter(visibleEnd)
    );
  }, [areaMaterialSnapshots, visibleStart, visibleEnd]);

  return (
    <Box sx={{ width: "100%", pt: 0 }}>
      <Box
        ref={timelineRef}
        sx={{ position: "relative", mb: 1 }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {visibleLanes.length > 0 && visibleDuration > 1 && visibleStart ? (
          <Stack divider={<Divider />} spacing={1}>
            {visibleLanes.map(({ lane, events: laneEvents }) => (
              <VideoTimelineEventLane
                key={lane ?? ""}
                lane={lane}
                isHighlighted={lane !== undefined && lane === highlightedLane}
                events={laneEvents}
                videoStart={visibleStart}
                duration={visibleDuration}
                activeEventIds={activeEventIds}
                selectedEventId={selectedEventId}
                onEventClick={onEventClick}
                colorEventsBy={colorEventsBy}
                materialNameSource={materialNameSource}
                showShortLabels={showShortLabels}
                shortLabelDetail={shortLabelDetail}
                modifiedEventIds={modifiedEventIds}
                adjacentPreviewById={adjacentPreviewById}
                highlightColorsByEventId={highlightColorsByEventId}
              />
            ))}
          </Stack>
        ) : null}
        <Box sx={{ position: "relative", width: "100%", pb: 4 }}>
          {visibleSnapshots &&
          visibleSnapshots.length > 0 &&
          visibleDuration > 1 &&
          visibleStart
            ? visibleSnapshots.map((snapshot) => {
                const positionSeconds = snapshot.timestamp.diff(
                  visibleStart,
                  "second"
                );
                return (
                  <TimelineAreaMappingMarker
                    key={snapshot.timestamp.unix()}
                    location={location}
                    snapshot={snapshot}
                    positionSeconds={positionSeconds}
                    duration={visibleDuration}
                    onClick={
                      onMappingSnapshotClick
                        ? () => {
                            onMappingSnapshotClick(snapshot);
                          }
                        : undefined
                    }
                    isSelected={
                      selectedSnapshotTimestamp === snapshot.timestamp.unix()
                    }
                  />
                );
              })
            : null}
          {readOnly ? null : (
            <LiveTimelineCursor
              visibleStartSeconds={visibleStartSeconds}
              visibleDuration={visibleDuration}
              onSeek={onSeek}
              formatTime={formatTimeWithMillis}
            />
          )}
          {children}
          {videoStart ? (
            <TimelineMarkers
              videoStart={videoStart}
              visibleStart={readOnly ? visibleStart : undefined}
              visibleDuration={readOnly ? visibleDuration : undefined}
            />
          ) : null}
        </Box>
      </Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {zoomContext ? formatTime(visibleStartSeconds) : formatTime(0)}
        </Typography>
        {zoomContext ? (
          <LiveTimeCaption
            visibleDuration={visibleDuration}
            formatTime={formatTimeWithMillis}
          />
        ) : null}
        <Typography variant="caption" color="text.secondary">
          {zoomContext
            ? formatTime(visibleStartSeconds + visibleDuration)
            : formatTime(duration)}
        </Typography>
      </Stack>
    </Box>
  );
};
